// Band-aware CTC breakup engine used by the HR offer builder for a live preview.
// Mirrors backend/src/utils/ctcEngine.js (kept in sync manually) - the backend
// recomputes the same breakup server-side and is the source of truth for what's stored.

export const CTC_BANDS = [
  { id: 'A', label: 'Band A (Entry, < 3L)', min: 0, max: 299999 },
  { id: 'B', label: 'Band B (Mid, 3L - 6L)', min: 300000, max: 599999 },
  { id: 'C', label: 'Band C (Senior, 6L+)', min: 600000, max: Infinity }
];

export const CTC_COMPONENTS = [
  { id: 'basic', label: 'Basic Salary', side: 'employee', basis: 'basicPercentOfCTC', rate: 0.40, locked: true },
  { id: 'hra', label: 'HRA', side: 'employee', basis: 'percentOfBasic', rate: 0.50 },
  { id: 'conveyance', label: 'Conveyance Allowance', side: 'employee', basis: 'fixed', amount: 19200 },
  { id: 'medical', label: 'Medical Allowance', side: 'employee', basis: 'fixed', amount: 15000 },
  { id: 'lta', label: 'LTA', side: 'employee', basis: 'percentOfBasic', rate: 0.0833 },
  { id: 'mealCoupons', label: 'Meal Coupons / Sodexo', side: 'employee', basis: 'fixed', amount: 26400 },
  { id: 'telephoneInternet', label: 'Telephone & Internet Reimbursement', side: 'employee', basis: 'fixed', amount: 12000 },
  { id: 'fuelCar', label: 'Fuel / Car Allowance', side: 'employee', basis: 'fixed', amount: 60000 },
  { id: 'specialAllowance', label: 'Special Allowance', side: 'employee', basis: 'balance', locked: true },
  { id: 'employerPF', label: 'Employer PF Contribution', side: 'employer', basis: 'percentOfBasic', rate: 0.12 },
  { id: 'employerESI', label: 'Employer ESI Contribution', side: 'employer', basis: 'percentOfCTC', rate: 0.0325 },
  { id: 'gratuity', label: 'Gratuity (Employer Contribution)', side: 'employer', basis: 'percentOfBasic', rate: 0.0481 },
  { id: 'bonus', label: 'Statutory / Ex-Gratia Bonus', side: 'employer', basis: 'percentOfBasic', rate: 0.0833 },
  { id: 'employerInsurance', label: 'Employer Insurance (GMC/GPA/GTLI)', side: 'employer', basis: 'fixed', amount: 18000 },
  { id: 'employerNPS', label: 'Employer NPS Contribution', side: 'employer', basis: 'percentOfBasic', rate: 0.10 }
];

// true = visible by default in that band; false = hidden by default (HR can still override per offer)
export const CTC_DEFAULT_VISIBILITY = {
  basic: { A: true, B: true, C: true },
  hra: { A: true, B: true, C: true },
  conveyance: { A: true, B: true, C: true },
  medical: { A: false, B: true, C: true },
  lta: { A: false, B: true, C: true },
  mealCoupons: { A: false, B: true, C: true },
  telephoneInternet: { A: false, B: true, C: true },
  fuelCar: { A: false, B: false, C: true },
  specialAllowance: { A: true, B: true, C: true },
  employerPF: { A: true, B: true, C: true },
  employerESI: { A: true, B: false, C: false },
  gratuity: { A: true, B: true, C: true },
  bonus: { A: true, B: true, C: false },
  employerInsurance: { A: false, B: true, C: true },
  employerNPS: { A: false, B: false, C: true }
};

export function detectCTCBand(ctc) {
  return CTC_BANDS.find(band => ctc >= band.min && ctc <= band.max) || CTC_BANDS[CTC_BANDS.length - 1];
}

// isVisible(componentId) => boolean. Special Allowance and Basic are always effectively on
// (locked components ignore isVisible) since they anchor the rest of the structure.
export function computeCTC(ctcInput, isVisible) {
  const ctc = Number(ctcInput) || 0;
  const values = {};

  const basicComponent = CTC_COMPONENTS.find(c => c.id === 'basic');
  const basic = ctc * basicComponent.rate;
  values.basic = basic;

  let employeeAllocated = basic;
  let employerAllocated = 0;

  CTC_COMPONENTS.forEach(component => {
    if (component.id === 'basic' || component.id === 'specialAllowance') return;

    const visible = component.locked || isVisible(component.id);
    let value = 0;
    if (visible) {
      if (component.basis === 'fixed') value = component.amount;
      else if (component.basis === 'percentOfBasic') value = basic * component.rate;
      else if (component.basis === 'percentOfCTC') value = ctc * component.rate;
    }

    values[component.id] = value;
    if (component.side === 'employee') employeeAllocated += value;
    else employerAllocated += value;
  });

  // If every visible component's mandatory amount already exceeds the entered CTC,
  // Special Allowance can't absorb the difference (it floors at 0) and the breakup
  // would silently total more than ctcInput. Surface that instead of hiding it.
  const overAllocatedBy = Math.max(employeeAllocated + employerAllocated - ctc, 0);
  const specialAllowance = Math.max(ctc - employeeAllocated - employerAllocated, 0);
  values.specialAllowance = specialAllowance;
  employeeAllocated += specialAllowance;

  const employeeTotal = employeeAllocated;
  const employerTotal = employerAllocated;

  return {
    values,
    employeeTotal,
    employerTotal,
    grandTotal: employeeTotal + employerTotal,
    isOverAllocated: overAllocatedBy > 0,
    overAllocatedBy
  };
}

// Resolves visibility for a component: candidate-specific overrides win, otherwise
// fall back to the band's default.
export function resolveVisibility(componentId, bandId, overrides) {
  if (overrides && componentId in overrides) return overrides[componentId];
  return CTC_DEFAULT_VISIBILITY[componentId]?.[bandId] ?? false;
}
